import React, { useState, useMemo } from 'react';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import { HttpService } from 'core/services/http';
import JobseekerHeader from './JobseekerHeader';
import {
  Box,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useTheme,
  useMediaQuery
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import DescriptionIcon from '@mui/icons-material/Description';

const drawerWidth = 260;

const navItems = [
  { label: 'Dashboard', icon: <DashboardIcon />, path: '/jobseeker/dashboard' },
  { label: 'My Profile', icon: <PersonIcon />, path: '/jobseeker/profile' },
  { label: 'My Applications', icon: <WorkIcon />, path: '/jobseeker/applications' },
  { label: 'My CVs', icon: <DescriptionIcon />, path: '/jobseeker/cvs' },
];

const JobseekerDashboardLayout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const user = useMemo(() => HttpService.getUser?.() ?? null, []);

  const handleDrawerToggle = () => {
    setMobileOpen((prev) => !prev);
  };

  const handleNavigate = (path) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: 3, py: 2.5 }}>
        <Typography variant="caption" fontWeight={700} color="primary" sx={{ letterSpacing: 0.8 }}>
          JOB FLOW CONNECT
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 0.25 }}>
          Jobseeker Portal
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap display="block">
          {user?.email ?? 'Signed in'}
        </Typography>
      </Box>
      <Divider />
      <List sx={{ flexGrow: 1, py: 1.5, px: 1.5 }}>
        {navItems.map((item) => {
          const isActive = location.pathname === item.path ||
            (item.path !== '/jobseeker/dashboard' && (
              location.pathname.startsWith(item.path) ||
              (item.path === '/jobseeker/profile' && location.pathname === '/profile')
            ));
          return (
            <ListItemButton
              key={item.path}
              selected={isActive}
              onClick={() => handleNavigate(item.path)}
              sx={{
                borderRadius: 1.5,
                mb: 0.5,
                '&.Mui-selected': {
                  bgcolor: 'primary.main',
                  color: 'white',
                  '& .MuiListItemIcon-root': { color: 'white' },
                  '&:hover': { bgcolor: 'primary.dark' },
                },
                '&:not(.Mui-selected):hover': { bgcolor: 'action.hover' },
              }}
            >
              <ListItemIcon sx={{ color: isActive ? 'white' : 'text.secondary', minWidth: 38 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ variant: 'body2', fontWeight: isActive ? 600 : 400 }}
              />
            </ListItemButton>
          );
        })}
      </List>
      <Divider />
      <Box sx={{ px: 2.5, py: 2 }}>
        <Typography variant="caption" color="text.disabled" display="block">
          Need help?
        </Typography>
        <Typography variant="caption" color="text.secondary">
          support@jobflowconnect.com
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'grey.50' }}>
      {/* Header - shows on both mobile and desktop */}
      <JobseekerHeader
        onMenuClick={handleDrawerToggle}
        drawerWidth={drawerWidth}
      />

      {/* Sidebar Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        aria-label="jobseeker navigation"
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth
            }
          }}
        >
          {drawer}
        </Drawer>
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth
            }
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Toolbar />
        <Box sx={{ flexGrow: 1, p: { xs: 2.5, md: 3.5 }, pt: { xs: 2, md: 2.5 } }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default JobseekerDashboardLayout;

